import { useReactToPrint } from "react-to-print";
import ButtonComponent from "../components/ButtonComponent";
import { CONSTANTS, PDF_PAGE_STYLE, PDF_TITLE, SIZES } from "../misc/constants";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useGetKBBReportQuery,
  useRefreshKBBReportMutation,
} from "../store/reducer/vehicle";
import Loader from "../components/Loader";
import ErrorPage from "./ErrorPage";
import { appendQueryParams } from "../misc/api.utils";
import KbbReportViewComponent from "../components/KbbViewComponent";
import { Box } from "@mui/material";
import { IVehicleKBBReport } from "../ts/kbb.interface";

const KbbReport = () => {
  const params = useParams();
  const {
    data,
    isLoading: isGetKBBCIP,
    isError: isGetKBBError,
  } = useGetKBBReportQuery(
    appendQueryParams(
      { vehicle_ids: params?.vehicleId?.split("&").map((v) => +v) || [] },
      false
    )
  );
  const [
    refreshKbbValues,
    { isLoading: isUpdateKBBCIP, isError: isUpdateKBBError },
  ] = useRefreshKBBReportMutation();

  const promiseResolveRef = useRef<any>(null);
  const reference = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      // Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const handlePrint = useReactToPrint({
    content: () => reference.current,
    documentTitle: PDF_TITLE.KBB_REPORT,
    pageStyle: PDF_PAGE_STYLE.PORTRAIT,
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true);
      });
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsPrinting(false);
    },
  });

  const handleRefreshReport = () => {
    refreshKbbValues({ id: params?.vehicleId });
  };

  if (isUpdateKBBError || isGetKBBError) {
    return <ErrorPage />;
  }

  return (
    <div className="background-white w-full">
      {isGetKBBCIP || isUpdateKBBCIP ? (
        <div className="flex justify-center items-center h-screen">
          <Loader />
        </div>
      ) : data?.data?.length == 1 ? (
        <div
          className="max-w-6xl bg-[#f6eee3] w-full h-full mx-auto p-8"
          ref={reference}
        >
          {data.data[0].kbb_report.vehicleId !== "0" ? (
            <Box>
              <KbbReportViewComponent vehicle={data.data[0]} />
              {!isPrinting && (
                <div className="flex justify-between my-4">
                  <ButtonComponent
                    onClick={handleRefreshReport}
                    type={CONSTANTS.RESTART}
                    text="Refresh Report"
                    color={CONSTANTS.TERTIARY}
                    size={SIZES.SMALL}
                  />
                  <ButtonComponent
                    onClick={handlePrint}
                    type={CONSTANTS.PRINT}
                    text="Print Report"
                    color={CONSTANTS.TERTIARY}
                    size={SIZES.SMALL}
                  />
                </div>
              )}
            </Box>
          ) : (
            <KbbReportError vehicle={data.data[0]} />
          )}
        </div>
      ) : (
        <div>
          <div className="max-w-6xl mx-auto flex justify-end my-4">
            <ButtonComponent
              onClick={handlePrint}
              type={CONSTANTS.PRINT}
              text="Print"
              color={CONSTANTS.TERTIARY}
              size={SIZES.SMALL}
            />
          </div>

          <div ref={reference}>
            {data.data.map((v: any, i: number) => {
              return (
                <div className="max-w-6xl bg-[#f6eee3] w-full h-full mx-auto my-6 p-6 break-before-page">
                  {v.kbb_report.vehicleId !== "0" ? (
                    <KbbReportViewComponent vehicle={v} key={i} />
                  ) : (
                    <KbbReportError vehicle={v} key={i} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const KbbReportError = (vehicle: any) => {
  const vehicleData = vehicle.vehicle;
  const kbbData: IVehicleKBBReport = vehicle.vehicle?.kbb_report;
  const kbbReportUpdatedAt = new Date(+kbbData?.updated_at * 1000);
  const printedAt = kbbReportUpdatedAt.toLocaleDateString("en-gb");

  return (
    <div>
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xl font-semibold">Spectrum Financial</div>
          <div>Lending/Suggested Retail Breakdown</div>
        </div>
        <div className="text-sm">Printed on: {printedAt || "--"}</div>
      </div>
      <div className="flex justify-between items-start mt-8 mb-2">
        <div>
          <div className="font-bold">VIN: {vehicleData?.vin || "--"}</div>
          <div className="text-sm">License Plate #: --</div>
        </div>
        <div>
          <div className="text-sm font-bold">
            Stock #: {vehicleData?.stock_number || "--"}
          </div>
          {vehicleData.auction_vehicle ? (
            <div className="text-sm">
              Lane: {vehicleData.auction_vehicle[0].lane} | Run:{" "}
              {vehicleData.auction_vehicle[0].run}
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
      <div className="flex justify-between border-t-4 border-b-2 py-2 border-tertiary-300 font-semibold">
        <div>Vehicle Info</div>
        <div>Invalid vehicle VIN or Vehicle details not found on KBB</div>
      </div>
    </div>
  );
};

export default KbbReport;
